import * as React from 'react';
import cn from 'classnames';
import { useIntl } from 'react-intl';
import { useHistory, useLocation } from 'react-router-dom';
import { useAtom } from 'jotai';
import { useAtomValue, useUpdateAtom } from 'jotai/utils';

import {
  ArrowRightCircle,
  ChatAI,
  MagnifyingGlass,
  X,
} from 'src/common/icons';
import { config } from 'src/utils/config';
import { addonsSettingsAtom } from 'src/shared/settings/addons';
import { useFeatureAvailability } from 'src/hooks/useFeatureAvailability';
import { chatOpenAtom, chatStreamingAtom } from 'src/shared/chat';
import useAiAttention from 'src/hooks/useAiAttention';
import { KEEP_OVERLAY_ATTR } from 'src/hooks/useShellOverlay';
import {
  dashboardFiltersAtom,
  setFilterAtom,
} from 'src/shared/dashboardFilters';
import { useChatSend } from 'src/chat/useChatSend';

import messages from './messages';
import styles from './PromptBar.module.scss';

type PromptMode = 'search' | 'ai';

const MODE_STORAGE_KEY = 'filespin.dashboard.promptbar.mode';

// AI Agent is the primary surface; search is the explicit alternative.
// Existing users with a persisted choice keep that choice — only the
// fallback for first-time users / cleared storage flips to 'ai'.
function readPersistedMode(): PromptMode {
  try {
    const v = localStorage.getItem(MODE_STORAGE_KEY);
    if (v === 'ai' || v === 'search') return v;
  } catch {
    /* localStorage unavailable */
  }
  return 'ai';
}

function writePersistedMode(mode: PromptMode) {
  try {
    localStorage.setItem(MODE_STORAGE_KEY, mode);
  } catch {
    /* localStorage unavailable — silent no-op */
  }
}

const EXAMPLE_ROTATE_MS = 3500;

/**
 * Universal prompt bar with an explicit AI Agent / Search segmented
 * toggle. AI Agent is the primary mode (the agent can ask, search, run
 * tools, and drive the UI); Search is the plain-text-match alternative
 * for power users who already know the keyword.
 *
 * Search mode -> writes `dashboardFiltersAtom.keyword` and (if not on
 *                `/assets`) navigates there. The per-page SearchBar
 *                picks up the change via the existing
 *                `useDebouncedFilterField` sync effect.
 * AI mode    -> calls `useChatSend()` and opens ChatSidebar. The send
 *                pipeline is the exact one ChatInput uses, so the
 *                response streams into the existing thread.
 *
 * A single trailing arrow button (or Enter) commits the prompt. There
 * is no separate "open chat" affordance here — that lives in the
 * AppHeader's right cluster as the AI sparkle toggle so users can
 * open/close the conversation without typing.
 */
export const PromptBar: React.FC = () => {
  const { formatMessage } = useIntl();
  const history = useHistory();
  const { pathname } = useLocation();

  const addonsSettings = useAtomValue(addonsSettingsAtom);
  const aiAddonEnabled =
    addonsSettings?.AI_ASSISTANT?.added && addonsSettings?.AI_ASSISTANT?.enabled;
  const agentStatus = useFeatureAvailability('ai-agent');
  // Built-in feature: the segmented AI/Search toggle is shown whenever chat
  // is enabled for this deployment. The AI segment itself is only *usable*
  // (selectable) when the account has the addon and the backend API is
  // available — otherwise it renders disabled and the bar stays in Search.
  const showSegment = Boolean(config.chatEnabled);
  const aiAvailable = Boolean(
    config.chatEnabled && aiAddonEnabled && agentStatus !== 'unavailable',
  );

  const [filterState] = useAtom(dashboardFiltersAtom);
  const setFilter = useUpdateAtom(setFilterAtom);
  const [chatOpen, setChatOpen] = useAtom(chatOpenAtom);
  const isStreaming = useAtomValue(chatStreamingAtom);
  const { send: sendChat, stop: stopChat } = useChatSend();
  // #932: subtle pulse + idle placeholder rotation while the AI Agent has
  // credits and the user hasn't engaged with it yet.
  const { active: aiAttention, dismiss: dismissAiAttention } = useAiAttention();

  // Don't pre-flip to 'search' based on `showAiMode` here: addons
  // settings hydrate from a query and are briefly absent on first
  // render, which would otherwise strand persisted 'ai' users on
  // Search even once AI becomes available. The effect below handles
  // the truly-unavailable case in one direction.
  const [mode, setMode] = React.useState<PromptMode>(() => readPersistedMode());
  const [draft, setDraft] = React.useState('');
  const [submitting, setSubmitting] = React.useState(false);
  const [focused, setFocused] = React.useState(false);
  const [exampleIndex, setExampleIndex] = React.useState(0);
  const inputRef = React.useRef<HTMLInputElement>(null);

  // If AI addon is disabled at runtime, force mode back to search.
  // Gate on `addonsSettings` being defined: the atom is `undefined`
  // until /api/v1/settings hydrates it, and during that window
  // `showAiMode` reads false. Without this guard we'd strand
  // persisted-'ai' users on Search on every refresh — the effect is
  // one-way, so once we flip we never flip back.
  React.useEffect(() => {
    if (addonsSettings === undefined) return;
    if (!aiAvailable && mode === 'ai') setMode('search');
  }, [aiAvailable, mode, addonsSettings]);

  // Search mode mirrors the committed filter keyword so the bar stays
  // in sync with chips / per-page SearchBar / AI intents. Coerce to a
  // string so a missing/undefined keyword (e.g. transient state from a
  // partial filter patch) never desyncs React's controlled input.
  React.useEffect(() => {
    if (mode !== 'search') return;
    const next = filterState.keyword ?? '';
    setDraft((cur) => (cur === next ? cur : next));
  }, [filterState.keyword, mode]);

  // Cmd/Ctrl-K -> focus the bar; Esc -> blur it.
  React.useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      const isFocus = (e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k';
      if (isFocus) {
        e.preventDefault();
        inputRef.current?.focus();
        inputRef.current?.select();
        return;
      }
      if (e.key === 'Escape' && document.activeElement === inputRef.current) {
        inputRef.current?.blur();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  // Rotate example placeholders while the input is focused and empty.
  // Pauses as soon as the user types — the browser hides the placeholder
  // during typing anyway, but stopping the timer avoids background work and
  // surprise re-renders. #932: while AI attention is active the rotation
  // also runs unfocused, so the resting bar invites with concrete examples.
  const rotationActive =
    (focused || (aiAttention && mode === 'ai')) && draft.trim() === '';
  React.useEffect(() => {
    if (!rotationActive) return;
    const id = window.setInterval(() => {
      setExampleIndex((i) => (i + 1) % 5);
    }, EXAMPLE_ROTATE_MS);
    return () => window.clearInterval(id);
  }, [rotationActive, mode]);

  // Reset to the first example whenever the user enters the bar fresh,
  // so the rotation always starts from a predictable example.
  React.useEffect(() => {
    if (rotationActive) setExampleIndex(0);
  }, [rotationActive]);

  const selectMode = (next: PromptMode) => {
    if (next === 'ai' && !aiAvailable) return;
    setMode(next);
    writePersistedMode(next);
    inputRef.current?.focus();
  };

  const submit = async () => {
    const text = draft.trim();
    if (!text || submitting) return;

    if (mode === 'search') {
      setFilter({ keyword: text, page: 1, hash: '' });
      if (!pathname.startsWith('/assets')) history.push('/assets');
      // Bar stays populated so the user can refine the search.
      return;
    }

    // AI mode
    setSubmitting(true);
    try {
      if (!chatOpen) setChatOpen(true);
      await sendChat(text);
      // Clear the input — the message now lives in the chat thread.
      setDraft('');
      // First engagement ends the attention pulse / coach mark for good.
      dismissAiAttention();
    } finally {
      setSubmitting(false);
    }
  };

  const clear = () => {
    setDraft('');
    if (mode === 'search') {
      setFilter({ keyword: '', page: 1, hash: '' });
    }
    inputRef.current?.focus();
  };

  const aiExamples = [
    messages.exampleAi1,
    messages.exampleAi2,
    messages.exampleAi3,
    messages.exampleAi4,
    messages.exampleAi5,
  ];
  const searchExamples = [
    messages.exampleSearch1,
    messages.exampleSearch2,
    messages.exampleSearch3,
    messages.exampleSearch4,
    messages.exampleSearch5,
  ];
  const staticPlaceholder = formatMessage(
    mode === 'ai' ? messages.placeholderAi : messages.placeholderSearch,
  );
  const placeholder = rotationActive
    ? formatMessage(
        (mode === 'ai' ? aiExamples : searchExamples)[exampleIndex % 5],
      )
    : staticPlaceholder;
  const ariaLabel = formatMessage(
    mode === 'ai' ? messages.ariaAsk : messages.ariaSearch,
  );

  return (
    <div
      className={cn(styles.bar, {
        [styles.barAttention]: aiAttention && mode === 'ai',
      })}
      role="search"
      aria-label={ariaLabel}
      data-test="prompt-bar"
      data-mode={mode}
      /* Mode decides whether an open asset overlay should get out of the way.
         An AI prompt is answered in the chat panel, which sits beside the
         overlay — closing the asset the user is asking about would be perverse.
         A search rewrites the grid the overlay is covering, so there the
         overlay must dismiss for the results to be seen. */
      {...(mode === 'ai' ? { [KEEP_OVERLAY_ATTR]: true } : {})}
    >
      {showSegment && (
        <div className={styles.segment} role="tablist" aria-label={ariaLabel}>
          <button
            type="button"
            role="tab"
            aria-selected={mode === 'ai'}
            aria-disabled={!aiAvailable}
            tabIndex={mode === 'ai' ? 0 : -1}
            className={cn(styles.segmentBtn, {
              [styles.segmentBtnActive]: mode === 'ai',
              [styles.segmentBtnDisabled]: !aiAvailable,
              // invite switching when attention is active but Search is selected
              [styles.segmentBtnAttention]: aiAttention && mode !== 'ai',
            })}
            onClick={() => selectMode('ai')}
            disabled={!aiAvailable}
            title={
              aiAvailable
                ? undefined
                : 'Not enabled — get in touch with us to upgrade your account for this feature.'
            }
            data-test="prompt-bar-mode-ai"
          >
            <ChatAI aria-hidden className={styles.segmentBtnIcon} />
            <span>{formatMessage(messages.aiMode)}</span>
          </button>
          <button
            type="button"
            role="tab"
            aria-selected={mode === 'search'}
            tabIndex={mode === 'search' ? 0 : -1}
            className={cn(styles.segmentBtn, {
              [styles.segmentBtnActive]: mode === 'search',
            })}
            onClick={() => selectMode('search')}
            data-test="prompt-bar-mode-search"
          >
            <MagnifyingGlass aria-hidden className={styles.segmentBtnIcon} />
            <span>{formatMessage(messages.searchMode)}</span>
          </button>
        </div>
      )}

      {/* Standalone leading icon. On desktop with the segmented control
          visible this is hidden via CSS (the active segment button
          already carries the mode icon). It re-appears on narrow
          widths where the segment collapses, and when the AI addon is
          disabled (no segment rendered at all). */}
      <span className={styles.iconLeft} aria-hidden>
        {mode === 'ai' ? <ChatAI /> : <MagnifyingGlass />}
      </span>

      <input
        ref={inputRef}
        type="text"
        autoComplete="off"
        spellCheck={false}
        className={cn(styles.input, {
          [styles.inputRotating]: rotationActive,
        })}
        placeholder={placeholder}
        value={draft ?? ''}
        onChange={(e) => setDraft(e.target.value)}
        onFocus={() => setFocused(true)}
        onBlur={() => setFocused(false)}
        onKeyDown={(e) => {
          if (e.key === 'Enter') {
            e.preventDefault();
            void submit();
          }
        }}
        aria-label={ariaLabel}
        data-test="prompt-bar-input"
        disabled={submitting}
      />

      {draft && (
        <button
          type="button"
          className={styles.clear}
          onClick={clear}
          aria-label={formatMessage(messages.clear)}
          data-test="prompt-bar-clear"
        >
          <X />
        </button>
      )}

      {mode === 'ai' && isStreaming ? (
        <button
          type="button"
          className={styles.submit}
          onClick={stopChat}
          aria-label="Stop generating"
          title="Stop generating"
          data-test="prompt-bar-stop"
        >
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" aria-hidden>
            <rect x="6" y="6" width="12" height="12" rx="2" fill="currentColor" />
          </svg>
        </button>
      ) : (
        <button
          type="button"
          className={styles.submit}
          onClick={() => void submit()}
          aria-label={formatMessage(messages.submit)}
          title={formatMessage(messages.submit)}
          data-test="prompt-bar-submit"
          disabled={!draft.trim() || submitting}
        >
          <ArrowRightCircle />
        </button>
      )}
    </div>
  );
};

export default PromptBar;
